// src/pages/weight-dispute/BulkActionsUploadModal.jsx
//
// "Bulk Upload Actions" for the merchant Weight Disputes list. A merchant who has many OPEN
// discrepancies uploads ONE CSV instead of opening each dispute: every row names an AWB and an
// action (ACCEPT / DISPUTE) and we act on it, one row at a time.
//
// Wire flow mirrors the detail page exactly (in spirit):
//   resolve AWB -> dispute (fetchDisputes pageSize 1)  ->  is it OPEN?
//     ACCEPT  : acceptDispute(current revision)
//     DISPUTE : addUrlEvidence -> find the new own URL evidence id -> raiseDispute([id], expRev)
//
// Everything is client-side until "Run": we hand-roll the CSV parse (no dep), pre-flight the
// rows, then run SEQUENTIALLY so each wallet settlement lands in order. A row can never abort the
// batch — every row leaves with a verdict, and the merchant downloads a results CSV at the end.
import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  Upload,
  UploadCloud,
  FileText,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Play,
} from "lucide-react";
import {
  fetchDisputes,
  acceptDispute,
  addUrlEvidence,
  raiseDispute,
  errorMessageOf,
} from "./weightDisputeApi";

// ---------------------------------------------------------------- CSV contract

const REQUIRED_COLUMNS = ["awb_number", "action", "reason", "evidence_url", "notes"];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const HTTP_URL_RE = /^https?:\/\//i;

// Verdict vocabulary — a fixed set so the results table + counts stay in lockstep.
const VERDICT = {
  ACCEPTED: "ACCEPTED",
  DISPUTED: "DISPUTED",
  SKIPPED_NOT_OPEN: "SKIPPED — not open",
  SKIPPED_NO_DISPUTE: "SKIPPED — no dispute for this AWB",
  INVALID: "INVALID",
  FAILED: "FAILED",
};

// ---------------------------------------------------------------- CSV parse (hand-rolled)

// A minimal RFC-4180-ish reader. Handles: quoted fields, quotes escaped as "", commas and
// newlines INSIDE quotes, CRLF or LF line endings, and trailing blank lines. Returns a flat
// array of rows, each row an array of string cells.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  // Strip a leading UTF-8 BOM so the first header cell matches cleanly.
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  let pushedAny = false;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
    pushedAny = true;
  };

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1; // consume the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      endField();
    } else if (ch === "\n") {
      endRow();
    } else if (ch === "\r") {
      // CRLF: swallow the \r; the \n on the next iteration ends the row. A lone \r also ends it.
      if (src[i + 1] === "\n") {
        endRow();
        i += 1;
      } else {
        endRow();
      }
    } else {
      field += ch;
    }
  }
  // Flush the final field/row unless the file ended exactly on a newline (no dangling content).
  if (field.length > 0 || row.length > 0 || !pushedAny) {
    endRow();
  }

  // Drop wholly-blank rows (a common trailing-newline / spreadsheet artifact).
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

// Map a header row to column indexes by NAME (case-insensitive, any order). Missing required
// columns are reported so the merchant can fix the file rather than silently mis-parse.
function mapHeader(headerCells) {
  const norm = headerCells.map((h) => h.trim().toLowerCase());
  const index = {};
  REQUIRED_COLUMNS.forEach((col) => {
    index[col] = norm.indexOf(col);
  });
  const missing = REQUIRED_COLUMNS.filter((col) => index[col] === -1);
  return { index, missing };
}

const cellAt = (cells, idx) => (idx >= 0 && idx < cells.length ? String(cells[idx] ?? "").trim() : "");

// Turn parsed cells into typed, pre-validated row objects. `rowNumber` is the 1-based line the
// merchant sees (header is line 1, so the first data row is line 2). INVALID rows carry a reason
// and are excluded from the run but still surface in the results CSV.
function buildRows(dataRows, index) {
  const seenAwb = new Set();
  return dataRows.map((cells, i) => {
    const rowNumber = i + 2; // +1 to skip header, +1 for 1-based
    const awbNumber = cellAt(cells, index.awb_number);
    const rawAction = cellAt(cells, index.action);
    const action = rawAction.toUpperCase();
    const reason = cellAt(cells, index.reason);
    const evidenceUrl = cellAt(cells, index.evidence_url);
    const notes = cellAt(cells, index.notes);

    const base = {
      rowNumber,
      awbNumber,
      action,
      rawAction,
      reason,
      evidenceUrl,
      notes,
      valid: true,
      invalidReason: null,
      verdict: null,
      detail: "",
    };

    const invalid = (why) => ({ ...base, valid: false, invalidReason: why });

    if (!awbNumber) return invalid("blank awb");
    if (action !== "ACCEPT" && action !== "DISPUTE") {
      return invalid(`bad action "${rawAction || ""}" (use ACCEPT or DISPUTE)`);
    }
    if (action === "DISPUTE") {
      if (!reason) return invalid("missing reason (required for DISPUTE)");
      if (!evidenceUrl) return invalid("missing evidence_url (required for DISPUTE)");
      if (!HTTP_URL_RE.test(evidenceUrl)) {
        return invalid("bad url (must start with http:// or https://)");
      }
    }
    // Duplicate AWB check runs last so a row that is otherwise INVALID keeps its more specific
    // reason; the FIRST occurrence of an AWB wins and later duplicates are rejected.
    const key = awbNumber.toLowerCase();
    if (seenAwb.has(key)) return invalid("duplicate awb in file");
    seenAwb.add(key);

    return base;
  });
}

// ---------------------------------------------------------------- sample + results CSV

const csvEscape = (value) => {
  const s = value == null ? "" : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const SAMPLE_CSV = [
  REQUIRED_COLUMNS.join(","),
  ["1234567890", "ACCEPT", "", "", "Charge looks correct"].map(csvEscape).join(","),
  [
    "9876543210",
    "DISPUTE",
    "Carrier over-measured; actual packed weight was 480g",
    "https://drive.google.com/file/d/EXAMPLE/view",
    "Unboxing video attached",
  ]
    .map(csvEscape)
    .join(","),
].join("\r\n");

function triggerDownload(filename, text, mime = "text/csv") {
  const blobUrl = window.URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

// Original five columns + result/detail appended, so a merchant can diff against what they sent.
function buildResultsCsv(rows) {
  const header = [...REQUIRED_COLUMNS, "result", "detail"].join(",");
  const body = rows.map((r) =>
    [r.awbNumber, r.rawAction, r.reason, r.evidenceUrl, r.notes, r.verdict, r.detail]
      .map(csvEscape)
      .join(",")
  );
  return [header, ...body].join("\r\n");
}

// ---------------------------------------------------------------- evidence resolution

// Mirror of the detail page's helper: from the updated dispute, find the id of the URL evidence
// we just added — prefer an exact url match among the merchant's active URL rows, else the most
// recent one by uploadedAt.
function newestOwnUrlEvidenceId(dispute, url) {
  const mine = (dispute?.evidences || []).filter(
    (e) =>
      String(e?.evidenceKind).toUpperCase() === "URL" &&
      String(e?.uploadedByType).toUpperCase() === "MERCHANT" &&
      !e?.markedDeletedAt
  );
  const exact = mine.filter((e) => e.url === url);
  const pool = exact.length > 0 ? exact : mine;
  if (pool.length === 0) return null;
  const newest = pool.reduce((latest, e) =>
    new Date(e.uploadedAt || 0) >= new Date(latest.uploadedAt || 0) ? e : latest
  );
  return newest.evidenceId ?? null;
}

// ---------------------------------------------------------------- per-row runner

// Process one PRE-VALIDATED row end to end and return { verdict, detail }. Throwing is not
// allowed to escape — the caller treats any leak as FAILED, but we catch here so the message is
// server-shaped.
async function runRow(token, row) {
  try {
    const res = await fetchDisputes(token, {
      awbNumber: row.awbNumber,
      pageSize: 1,
      pageNumber: 0,
    });
    const dispute = Array.isArray(res?.weightDisputes) ? res.weightDisputes[0] : null;
    if (!dispute) return { verdict: VERDICT.SKIPPED_NO_DISPUTE, detail: "" };
    if (dispute.status !== "OPEN") {
      return { verdict: VERDICT.SKIPPED_NOT_OPEN, detail: `status ${dispute.status}` };
    }

    const disputeId = dispute.disputeId;
    const expectedRevisionNumber = dispute.revisionNumber ?? 1;

    if (row.action === "ACCEPT") {
      await acceptDispute(token, disputeId, undefined);
      return { verdict: VERDICT.ACCEPTED, detail: "" };
    }

    // DISPUTE — description is `reason`, with `notes` appended as a new paragraph when present.
    const description = row.notes ? `${row.reason}\n\n${row.notes}` : row.reason;
    const updated = await addUrlEvidence(token, disputeId, {
      url: row.evidenceUrl,
      description: "",
    });
    const evidenceId = newestOwnUrlEvidenceId(updated, row.evidenceUrl);
    if (!evidenceId) {
      return { verdict: VERDICT.FAILED, detail: "could not resolve the uploaded evidence" };
    }
    await raiseDispute(token, disputeId, {
      description,
      evidenceIds: [evidenceId],
      expectedRevisionNumber,
    });
    return { verdict: VERDICT.DISPUTED, detail: "" };
  } catch (e) {
    return { verdict: VERDICT.FAILED, detail: errorMessageOf(e) };
  }
}

// ---------------------------------------------------------------- verdict styling

const VERDICT_PILL = {
  [VERDICT.ACCEPTED]: "bg-slate-100 text-slate-700",
  [VERDICT.DISPUTED]: "bg-blue-100 text-blue-700",
  [VERDICT.SKIPPED_NOT_OPEN]: "bg-amber-100 text-amber-800",
  [VERDICT.SKIPPED_NO_DISPUTE]: "bg-amber-100 text-amber-800",
  [VERDICT.INVALID]: "bg-gray-200 text-gray-700",
  [VERDICT.FAILED]: "bg-rose-100 text-rose-700",
};
const verdictClass = (verdict) => VERDICT_PILL[verdict] || "bg-gray-100 text-gray-600";

// ---------------------------------------------------------------- modal shell (local)

function ModalShell({ children, onClose, maxWidth = "max-w-2xl" }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 8 }}
        className={`w-full ${maxWidth} overflow-hidden rounded-2xl bg-white shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------- main component

// Three phases in one modal: "choose" (info + file picker + pre-flight) → "running" (live
// progress, no close) → "done" (verdict counts + per-row table + results download). Closing is
// blocked only while running.
export default function BulkActionsUploadModal({ getAuthToken, onClose, onDone }) {
  const [phase, setPhase] = useState("choose"); // choose | running | done
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [rows, setRows] = useState([]); // built + validated rows (valid and invalid)
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => !r.valid), [rows]);

  const actionBreakdown = useMemo(() => {
    let accept = 0;
    let dispute = 0;
    validRows.forEach((r) => {
      if (r.action === "ACCEPT") accept += 1;
      else if (r.action === "DISPUTE") dispute += 1;
    });
    return { accept, dispute };
  }, [validRows]);

  const resetSelection = () => {
    setRows([]);
    setParseError(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFileChosen = (file) => {
    setFileError(null);
    setParseError(null);
    setFileName("");
    setRows([]);
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setFileError("That file is over 5 MB. Please upload a smaller CSV.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const parsed = parseCsv(text);
        if (parsed.length === 0) {
          setParseError("The file is empty.");
          return;
        }
        const { index, missing } = mapHeader(parsed[0]);
        if (missing.length > 0) {
          setParseError(
            `Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}. ` +
              `The header row must include: ${REQUIRED_COLUMNS.join(", ")}.`
          );
          return;
        }
        const dataRows = parsed.slice(1);
        if (dataRows.length === 0) {
          setParseError("No data rows found below the header.");
          return;
        }
        setRows(buildRows(dataRows, index));
      } catch (err) {
        setParseError(`Could not read the file: ${err?.message || "unknown error"}`);
      }
    };
    reader.onerror = () => setParseError("Could not read the file.");
    reader.readAsText(file);
  };

  const run = async () => {
    setPhase("running");
    setProgress(0);
    const token = getAuthToken?.();
    // Work on a local copy so we can mutate verdicts, then commit once at the end.
    const working = rows.map((r) => ({ ...r }));
    // Stamp INVALID rows up front — they are never sent, but they belong in the results CSV.
    working.forEach((r) => {
      if (!r.valid) {
        r.verdict = VERDICT.INVALID;
        r.detail = r.invalidReason || "";
      }
    });

    const toRun = working.filter((r) => r.valid);
    for (let i = 0; i < toRun.length; i += 1) {
      const row = toRun[i];
      // eslint-disable-next-line no-await-in-loop -- sequential by design: each accept settles a wallet
      const outcome = await runRow(token, row);
      row.verdict = outcome.verdict;
      row.detail = outcome.detail;
      setProgress(i + 1);
    }

    setRows(working);
    setPhase("done");
  };

  const doneCounts = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      const key = r.verdict || VERDICT.INVALID;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [rows]);

  // Only "running" locks the modal shut; choose/done both allow a plain close/finish.
  const guardedClose = phase === "running" ? () => {} : phase === "done" ? onDone : onClose;

  return (
    <ModalShell onClose={guardedClose} maxWidth="max-w-2xl">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <Upload className="h-5 w-5 text-blue-600" />
          Bulk Upload Actions
        </h2>
        <button
          onClick={guardedClose}
          disabled={phase === "running"}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {phase === "choose" ? (
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {/* Info panel */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            <p className="font-medium">Prepare a CSV with these columns:</p>
            <code className="mt-1.5 block overflow-x-auto rounded-md bg-white/70 px-2.5 py-1.5 font-mono text-xs text-blue-800">
              {REQUIRED_COLUMNS.join(",")}
            </code>
            <p className="mt-2 text-xs leading-relaxed text-blue-800">
              <strong>Action:</strong> DISPUTE or ACCEPT. Only OPEN discrepancies are processed.
              For <strong>DISPUTE</strong>, <code className="font-mono">reason</code> and a valid{" "}
              <code className="font-mono">evidence_url</code> (starting http:// or https://) are
              required; <code className="font-mono">notes</code> is appended to the reason. For{" "}
              <strong>ACCEPT</strong>, the other fields are ignored.
            </p>
            <button
              type="button"
              onClick={() => triggerDownload("weight-dispute-bulk-sample.csv", SAMPLE_CSV)}
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
            >
              <Download className="h-3.5 w-3.5" />
              Download sample CSV
            </button>
          </div>

          {/* Drop-zone-style file picker */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-4 flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center transition hover:border-blue-400 hover:bg-blue-50/40"
          >
            <UploadCloud className="h-8 w-8 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Click to choose a CSV</span>
            <span className="text-xs text-gray-400">Max 5 MB</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => onFileChosen(e.target.files?.[0])}
          />

          {fileName ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <FileText className="h-4 w-4 text-gray-400" />
              <span className="truncate font-medium">{fileName}</span>
            </div>
          ) : null}

          {fileError ? (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{fileError}</span>
            </div>
          ) : null}

          {parseError ? (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{parseError}</span>
            </div>
          ) : null}

          {/* Pre-flight summary */}
          {rows.length > 0 ? (
            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-semibold text-gray-900">
                  {rows.length} {rows.length === 1 ? "row" : "rows"}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-slate-700">
                  <strong className="tabular-nums">{actionBreakdown.accept}</strong> accept
                </span>
                <span className="text-blue-700">
                  <strong className="tabular-nums">{actionBreakdown.dispute}</strong> dispute
                </span>
                {invalidRows.length > 0 ? (
                  <span className="text-rose-600">
                    <strong className="tabular-nums">{invalidRows.length}</strong> invalid
                  </span>
                ) : null}
              </div>

              {invalidRows.length > 0 ? (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                    Invalid rows (skipped)
                  </p>
                  <ul className="mt-1.5 max-h-40 space-y-1 overflow-y-auto text-xs text-gray-600">
                    {invalidRows.map((r) => (
                      <li key={r.rowNumber} className="flex gap-2">
                        <span className="font-mono font-semibold text-gray-500">
                          Row {r.rowNumber}
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>
                          {r.awbNumber ? (
                            <span className="font-mono text-gray-700">{r.awbNumber}</span>
                          ) : null}{" "}
                          {r.invalidReason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {phase === "running" ? (
        <div className="px-5 py-10">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm font-semibold text-gray-900">
              Processing {progress} / {validRows.length}…
            </p>
            <p className="text-xs text-gray-500">
              Each row is handled one at a time. Please keep this window open.
            </p>
            <div className="mt-1 h-2 w-full max-w-sm overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-blue-600 transition-all"
                style={{
                  width: `${validRows.length ? (progress / validRows.length) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {phase === "done" ? (
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
            <p className="text-sm font-semibold text-gray-900">
              Done — processed {rows.length} {rows.length === 1 ? "row" : "rows"}.
            </p>
          </div>

          {/* Verdict counts */}
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(doneCounts).map(([verdict, n]) => (
              <span
                key={verdict}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${verdictClass(
                  verdict
                )}`}
              >
                {verdict}
                <span className="tabular-nums">{n}</span>
              </span>
            ))}
          </div>

          {/* Per-row results table */}
          <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full table-fixed text-left text-xs">
                <colgroup>
                  <col className="w-[26%]" />
                  <col className="w-[16%]" />
                  <col className="w-[26%]" />
                  <col className="w-[32%]" />
                </colgroup>
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-gray-500">
                    <th className="px-3 py-2 font-semibold">AWB</th>
                    <th className="px-3 py-2 font-semibold">Action</th>
                    <th className="px-3 py-2 font-semibold">Verdict</th>
                    <th className="px-3 py-2 font-semibold">Reason / detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.rowNumber} className="align-top">
                      <td className="px-3 py-2 font-mono text-gray-800">{r.awbNumber || "-"}</td>
                      <td className="px-3 py-2 text-gray-600">{r.rawAction || "-"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${verdictClass(
                            r.verdict
                          )}`}
                        >
                          {r.verdict}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        <span className="line-clamp-2" title={r.detail || r.reason || ""}>
                          {r.detail || r.reason || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        {phase === "choose" ? (
          <>
            {rows.length > 0 ? (
              <button
                onClick={resetSelection}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            ) : null}
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={run}
              disabled={validRows.length === 0}
              title={validRows.length === 0 ? "Upload a CSV with at least one valid row" : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Play className="h-4 w-4" />
              Run {validRows.length} {validRows.length === 1 ? "action" : "actions"}
            </button>
          </>
        ) : null}

        {phase === "running" ? (
          <button
            disabled
            className="inline-flex cursor-not-allowed items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white opacity-60"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Running…
          </button>
        ) : null}

        {phase === "done" ? (
          <>
            <button
              onClick={() => triggerDownload("weight-dispute-bulk-results.csv", buildResultsCsv(rows))}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download results CSV
            </button>
            <button
              onClick={onDone}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Done
            </button>
          </>
        ) : null}
      </div>
    </ModalShell>
  );
}
